import { getActiveTab } from '@/store/modules/table/active-tab';
import {
  getTableQuery,
  getTableQueryTemporary,
  getTableActiveReports,
  getTableFilters,
  getTableModule,
} from '@/store/modules/table/accessors';
import { useStore } from '@/store';

export const getActiveReportIndex = (): number => {
  return getTableActiveReports().findIndex(report => report.tabId === getActiveTab());
};

export const getTableQueryForCurrentTab = (): Record<string, unknown> => {
  if (getActiveTab() !== 0) {
    const reports = getTableActiveReports();
    const index = getActiveReportIndex();
    return (reports[index]?.state?.tableQuery as Record<string, unknown>) || {};
  }
  return getTableQuery();
};

export const getTableQueryTemporaryForCurrentTab = (): Record<string, unknown> => {
  if (getActiveTab() !== 0) {
    const reports = getTableActiveReports();
    const index = getActiveReportIndex();
    return (reports[index]?.state?.tableQueryTemporary as Record<string, unknown>) || {};
  }
  return getTableQueryTemporary();
};

export const getTableQuerySearch = (): Record<string, unknown> => {
  const reportSearchSetting = useStore().user?.reportSearchSetting;

  if (getActiveTab() !== 0) {
    const reports = getTableActiveReports();
    const index = getActiveReportIndex();
    return (reports[index]?.state?.tableQuery?.search as Record<string, unknown>) || {};
  }

  switch (reportSearchSetting) {
    case 'perTab':
      return (getTableQuery()?.search?.[getTableQuery().groupBy as string] as Record<string, unknown>) || {};
    case 'allTabs':
    default:
      return (getTableQuery().search as Record<string, unknown>) || {};
  }
};

export const getTableQueryTemporarySearch = (): Record<string, unknown> => {
  const reportSearchSetting = useStore().user?.reportSearchSetting;

  if (getActiveTab() !== 0) {
    const reports = getTableActiveReports();
    const index = getActiveReportIndex();
    return (reports[index]?.state?.tableQueryTemporary?.search as Record<string, unknown>) || {};
  }

  switch (reportSearchSetting) {
    case 'perTab':
      return (
        (getTableQueryTemporary()?.search?.[getTableQuery()?.groupBy as string] as Record<string, unknown>) || {}
      );
    case 'allTabs':
    default:
      return (getTableQueryTemporary().search as Record<string, unknown>) || {};
  }
};

export const getTableColumnsFiltersForCurrentTab = (): Record<string, unknown> => {
  if (getActiveTab() !== 0) {
    const reports = getTableActiveReports();
    const index = getActiveReportIndex();
    return (reports[index]?.state?.filters as Record<string, unknown>) || {};
  }
  return getTableFilters();
};

export const getTableColumnsFiltersForCurrentTabInQueryFormat = () => {
  const filterHaving: string[] = [];
  const filterHavingOptions: unknown[] = [];
  const filterHavingValues: unknown[] = [];
  const filters = getTableColumnsFiltersForCurrentTab();

  Object.keys(filters).forEach((key) => {
    const filter = filters[key] as {
      filterHavingOptions?: unknown;
      filterHavingValues?: unknown;
    };
    filterHaving.push(key);
    filterHavingOptions.push(filter.filterHavingOptions);
    filterHavingValues.push(filter.filterHavingValues);
  });

  return { filterHaving, filterHavingOptions, filterHavingValues };
};

export const getTableSelectedItem = (): Record<string, unknown> => {
  const selectedItems = getTableModule().selectedItems;
  if (selectedItems.length !== 1) return {};

  const { DT_RowId, DT_RowName, DT_RowWorkspaceId, ...restProps } = selectedItems[0] as {
    DT_RowId: string;
    DT_RowName: string;
    DT_RowWorkspaceId: string;
    [key: string]: unknown;
  };

  return {
    id: DT_RowId,
    name: DT_RowName,
    workspaceId: DT_RowWorkspaceId,
    ...restProps,
  };
};

export const getTableSelectedItems = (): unknown[] => getTableModule().selectedItems;

export const getTableExportParams = (): Record<string, unknown> =>
  (getTableModule().exportParams as Record<string, unknown>) || {};

export const getTableCustomEvents = (): Record<string, unknown> => getTableModule().customEvents;

export const getTableDrilldownFilter = (): Bemob.ReportQueryDrill[] =>
  (getTableModule().drilldownFilter as Bemob.ReportQueryDrill[]) || [];

export const getTableDrilldown = (): unknown[] => (getTableModule().drilldown as unknown[]) || [];

export const getTablePreviewLink = (): string => String(getTableModule().previewLink ?? '');

export const getTableCustomGroups = (): Record<string, unknown> =>
  (getTableModule().customGroups as Record<string, unknown>) || {};

export const getTableIsHideFilters = (): boolean => getTableModule().isHideFilters;

export const getTableMenuOverride = (): string | null => getTableModule().menuOverride;

export const getTableNextReportGroup = (): unknown => getTableModule().nextReportGroup;

export const getTableNotesOpened = (): boolean => getTableModule().notesOpened;

export const getTableChartOpen = (): boolean => getTableModule().chartOpen;

export const getTableTotal = (): number => getTableModule().total;

export const getTableProgressBlockVisible = (): boolean => getTableModule().progressBlockVisible;

export const getTableNoReportBlockVisible = (): boolean => getTableModule().noReportBlockVisible;

export const getTableActiveTab = (): number => getTableModule().activeTab;

export const getTableLastActiveTab = (): unknown => getTableModule().lastActiveTab;

export const getTableEntityNames = (): Record<string, unknown> => getTableModule().entityNames;

export const getTableChartHiddenSeries = (): Record<string, unknown> =>
  getTableModule().chartHiddenSeries;

export const getTableChartHiddenSeriesDashboard = (): Record<string, unknown> =>
  getTableModule().chartHiddenSeriesDashboard;
